/**
 * PackPageShell — the one SHAPE every product's pack landing page renders (plan
 * 2026_09_01_pack_page_shell.md §2). Presentational only: no state hooks, no product import, no
 * stylesheet import of its own. The state machinery (the pack machine, the queue hook, the
 * consent/auth gates, entitlement, polling, muses) stays in each product's own pack-page
 * component, which builds the slot content below and hands it here.
 *
 * `styles` is the CSS-module injection pattern already used for the hub (`PackHubToolbar`,
 * `PackIndex`) — each product keeps its own pack-page stylesheet module and passes it straight
 * through; no class collision, no shared stylesheet.
 *
 * Render order below is the union of narcis's and ovid's identical section sequence (plan §1);
 * `rootTag`, `header`, `related`, `afterGallery`, `howItWorks`, `legalNote`, `footer`, `overlay`
 * are the only places a product's presence/absence actually varies the tree — everything else is
 * unconditional structure.
 */
import type {ReactNode} from 'react'

export type PackPageStyles = Record<string, string>

export interface PackPageSeoSection {
    /** React list key only — never rendered. */
    key: string
    title?: string
    paragraphs?: string[]
    steps?: {name: string; text: string}[]
    faq?: {q: string; a: string}[]
    bullets?: string[]
}

export interface PackPageShellProps {
    styles: PackPageStyles
    /** Default 'div' — plan §6 Q2. Toile is the one caller that passes 'main'. */
    rootTag?: 'div' | 'main'
    jsonLd?: object[]
    preloadImages?: (string | undefined)[]
    srOnly?: {h1: string; h2s: readonly string[]; paragraphs: readonly string[]}
    header: ReactNode
    /** Rendered twice verbatim — the mobile-hoisted header and the desktop aside. */
    eyebrow: ReactNode
    /** Rendered twice verbatim — the mobile-hoisted header and the desktop aside. */
    h1: ReactNode
    /** Narcis's/ovid's queue chip — sits between the desktop H1 and the tagline. */
    asideBeforeTagline?: ReactNode
    /** The `<p className={styles.detailHero__tagline}>` body — the shell owns the wrapping `<p>`. */
    tagline: ReactNode
    /** Also render the tagline in the mobile-hoisted header, under the H1, and hide the aside copy
     *  where that header shows — the promise before the pictures on the phone (plan
     *  2026_09_23_pack_stage_coda.md § Decided 6). Default false: the tagline stays in the aside
     *  at every width, as before. */
    taglineInMobileHeader?: boolean
    /** Everything below the tagline in the left column: muse row/panel, price row, the CTA slot,
     *  ghosts, notes, the step-strip/model-chip/muse-chip. One combined slot (plan §2) — the
     *  products' own internal ordering of these pieces varies by state, not by shape. */
    asideAfterTagline?: ReactNode
    /** `PackHeroStage` — the `.detailHero__right` body; the shell owns that wrapper `<div>`. */
    stage: ReactNode
    /** The product's `PackRelatedStrip`, already built. Absent only for the caller's own early
     *  return (ovid's delivered view builds its own strip outside the shell). */
    related?: ReactNode
    galleryAriaLabel: string
    galleryCaption: ReactNode
    /** `PackGalleryRows` (narcis/ovid) or the product's own grid (toile's `PackGrid`). */
    galleryBody: ReactNode
    /** The returning-buyer banner. */
    afterGallery?: ReactNode
    /** Absent or an empty `steps` array renders nothing — toile passes neither. */
    howItWorks?: {subhead: string; steps: {title: string; body: string}[]}
    /** Ends with the About block, built by the caller — see each product's own `ABOUT_*` const. */
    seoSections: PackPageSeoSection[]
    legalNote?: ReactNode
    /** Plan §6 Q1 — narcis passes nothing (`_layout.tsx` already renders `<Footer/>` after
     *  `<main>`); ovid and toile pass `<Footer/>` here. */
    footer?: ReactNode
    /** Rendered after the root element, outside `.page`/`.detailHero` entirely — the soft-confirm
     *  reset dialog. */
    overlay?: ReactNode
}

export function PackPageShell({
    styles,
    rootTag = 'div',
    jsonLd,
    preloadImages,
    srOnly,
    header,
    eyebrow,
    h1,
    asideBeforeTagline,
    tagline,
    taglineInMobileHeader = false,
    asideAfterTagline,
    stage,
    related,
    galleryAriaLabel,
    galleryCaption,
    galleryBody,
    afterGallery,
    howItWorks,
    seoSections,
    legalNote,
    footer,
    overlay,
}: PackPageShellProps) {
    const RootTag = rootTag
    return (
        <>
            {jsonLd?.map((schema, i) => (
                <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}} />
            ))}
            {preloadImages?.filter((href): href is string => !!href).map((href, i) => (
                <link key={`${i}-${href}`} rel="preload" as="image" href={href} fetchPriority="high" />
            ))}
            {srOnly && (
                <div className="sr-only" aria-hidden="false">
                    <h2>{srOnly.h1}</h2>
                    {srOnly.h2s.map((h2, i) => (
                        <h2 key={i}>{h2}</h2>
                    ))}
                    {srOnly.paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}
                </div>
            )}

            <RootTag className={styles.page}>
                {header}

                <section className={styles.detailHero}>
                    <div className={styles.detailHero__mobileHeader}>
                        <p className={styles.detailHero__eyebrow}>{eyebrow}</p>
                        <h1 className={styles.detailHero__heading}>{h1}</h1>
                        {taglineInMobileHeader && <p className={styles.detailHero__tagline}>{tagline}</p>}
                    </div>
                    <div className={styles.detailHero__grid}>
                        <aside className={styles.detailHero__left}>
                            <p className={`${styles.detailHero__eyebrow} ${styles.detailHero__desktopOnly}`}>{eyebrow}</p>
                            <h1 className={`${styles.detailHero__heading} ${styles.detailHero__desktopOnly}`}>{h1}</h1>

                            {asideBeforeTagline}

                            <p
                                className={
                                    taglineInMobileHeader
                                        ? `${styles.detailHero__tagline} ${styles.detailHero__desktopOnly}`
                                        : styles.detailHero__tagline
                                }
                            >
                                {tagline}
                            </p>

                            {asideAfterTagline}
                        </aside>

                        <div className={styles.detailHero__right}>{stage}</div>
                    </div>
                </section>

                {related}

                <section className={styles.galleryProof} aria-label={galleryAriaLabel}>
                    <div className={styles.galleryProofInner}>
                        {galleryCaption}
                        {galleryBody}
                    </div>
                </section>

                {afterGallery}

                {howItWorks && howItWorks.steps.length > 0 && (
                    <div className={styles.belowWorkbench}>
                        <section className={styles.howSection}>
                            <h2 className={styles.howHeading}>How it works</h2>
                            <p className={styles.howSubhead}>{howItWorks.subhead}</p>
                            <div className={styles.howSteps}>
                                {howItWorks.steps.map((step, i) => (
                                    <div key={i} className={styles.howStep}>
                                        <p className={styles.howStepNumber}>{i + 1}</p>
                                        <h3 className={styles.howStepTitle}>{step.title}</h3>
                                        <p className={styles.howStepBody}>{step.body}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}

                <div className={styles.seoContent}>
                    {seoSections.map((section) => (
                        <section key={section.key} className={styles.seoSection}>
                            {section.title && <h2 className={styles.seoHeading}>{section.title}</h2>}
                            {section.paragraphs?.map((p, i) => (
                                <p key={i} className={styles.seoParagraph}>{p}</p>
                            ))}
                            {section.steps && (
                                <ol className={styles.seoPrepList}>
                                    {section.steps.map((step, i) => (
                                        <li key={i} className={styles.seoPrepItem}>
                                            <span className={styles.seoPrepName}>{step.name}</span>
                                            <span className={styles.seoPrepText}>{step.text}</span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                            {section.faq && (
                                <dl className={styles.seoFaqList}>
                                    {section.faq.map((item, i) => (
                                        <div key={i} className={styles.seoFaqItem}>
                                            <dt className={styles.seoFaqQ}>{item.q}</dt>
                                            <dd className={styles.seoFaqA}>{item.a}</dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                            {section.bullets && (
                                <ul className={styles.seoUseCasesList}>
                                    {section.bullets.map((item, i) => (
                                        <li key={i} className={styles.seoUseCasesItem}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    ))}
                </div>

                {legalNote ? <p className={styles.legalNote}>{legalNote}</p> : null}

                {footer}
            </RootTag>

            {overlay}
        </>
    )
}

/** The two verbatim gallery-rows renderers (narcis N:1131-1142 / ovid O:1311-1322, plan §2). */
export function PackGalleryRows({rows, altBySrc, styles}: {
    rows: readonly {label: string; portraits: readonly string[]}[]
    altBySrc: Map<string, string>
    styles: PackPageStyles
}) {
    return (
        <>
            {rows.map((row, i) => (
                <div key={i} className={styles.detailRow}>
                    <span className={styles.detailRow__label}>{row.label}</span>
                    <div className={styles.detailRow__grid}>
                        {row.portraits.map((src, j) => (
                            <div key={j} className={styles.detailRow__cell}>
                                <img src={src} alt={altBySrc.get(src) ?? ''} loading="lazy" />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </>
    )
}
